import { Injectable, ViewContainerRef } from "@angular/core";
import moment from "moment";
import { CheckInsRequestParam, MassCheckInsRequestParam } from "src/app/utils/interfaces/new/check-ins-request-param";
import { IdentityToCheck } from "src/app/utils/interfaces/new/identity-to-check";
import { InsIdentityInfos } from "src/app/utils/interfaces/new/ins-identity-infos.interface";
import { LectureIns } from "src/app/utils/interfaces/new/lecture-ins.interface";
import { environment } from "src/environments/environment";
import { CpxHandleServiceAbstract, SessionTypeEnum, TypeConnecteurEnum, WirelessModeCardType } from "../../cpx-handle/cpx-handle-service-abstract";
import { DoRetryError } from "../../cpx-handle/cpx-handle.service";
import { ActionEnum, DomainEnum, LogFonctionnelCallerService, LogFonctionnelService, LogObject, ResultEnum, TypeTargetEnum } from "../../log-fonctionnel/log-fonctionnel.service";
import { RessourceService } from "../../ressource/ressource.service";
import { CadreExerciceIsMissing, CannotCreateTlsiConnector, CannotRetreiveIns, MultipleIdentitiesFound, NoIdentityFound, NoIdentityInfos, NotSameVitaleCard } from "../ins.error";
import { InsServiceAbstract } from "../ins-service.abstract";
import { contentTypeCodeSettingKey, nomSettingKey, nomStructureSettingKey, practiceSettingKey, prenomSettingKey, professionSettingKey, specialiteSettingKey, typeStructureSettingKey } from "../../dmp/dmp.service";
import { StringUtilsService } from "../../string-utils/string-utils.service";
import { ReferentielService } from "../../referentiel/referentiel.service";
import { CpxSessionInterface } from "src/app/utils/interfaces/new/cpx-session.interface";
import { StellairService } from "../../stellair/stellair.service";
import { buildCheckInsRequestLogDetails, buildIdentityLogDetails, buildIdentityRequestLogParams, buildIdentitySearchLogDetails, buildInsiErrorLogDetails, buildInsiRequestLogDetails, CHECK_INS_LOG_SUBDOMAIN, INS_FROM_IDENTITY_LOG_SUBDOMAIN, INS_FROM_VITALE_LOG_SUBDOMAIN, INS_MULTIPLE_IDENTITIES_LOG_DETAILS, INS_NO_IDENTITY_LOG_DETAILS, INS_RETOUR_LOG_HEADER, INS_UNIQUE_IDENTITY_LOG_HEADER } from "./ins-log.utils";

@Injectable({
    providedIn: 'root'
})
export class InsWebSocketService extends InsServiceAbstract {

    _resourceId: string;

    private get _cpxInstance(): any {
        return this.__cpxInstance;
    }
    private set _cpxInstance(instance: any){
        this.__cpxInstance = instance;
    }
    private __cpxInstance: any;

    private _logCallSrv: LogFonctionnelCallerService;

    private LpsInfos =
        {
            s_idam: environment.iCanopeeIdam,
            s_numAM: environment.iCanopeeNumAM,
            s_version: environment.iCanopeeVersion,
            s_instance: environment.iCanopeeInstance,
            s_name: environment.iCanopeeName,
            s_billingNumber: this._formatBillingNumber(environment.iCanopeeBillingNumber)
        };

    private typeSession: SessionTypeEnum = SessionTypeEnum.CARTE;

    constructor(
        private _cpxHandlerSrv: CpxHandleServiceAbstract,
        private _resourceSrv: RessourceService,
        private _logSrv: LogFonctionnelService,
        private _strUtilsSrv: StringUtilsService,
        private _refSrv: ReferentielService,
        private _stellairSrv: StellairService,
    ) {
        super();
    }

    private _formatBillingNumber(billingNumber: string): string | undefined {
        if (!billingNumber || billingNumber === '') {
            return undefined;
        } else if (billingNumber.length === 10) {
            return billingNumber.slice(1)
        } else if (billingNumber.length === 9) {
            return billingNumber;
        } else {
            return undefined;
        }
    }

    async getInsFromIdentityInformation(view: ViewContainerRef, identityInfos: InsIdentityInfos): Promise<LectureIns> {
        try {
            if (!await this.createTlsiConnector(view)) {
                return undefined;
            }
        } catch (e) {
            console.error(e);
            if (e instanceof DoRetryError) {
                return await this.getInsFromIdentityInformation(view, identityInfos);
            } else if (e && e.message && e.message === 'Le cadre d\'exercice n\'est pas renseigné dans les paramètres') {
                throw new CadreExerciceIsMissing(e.message);
            } else {
                throw new CannotCreateTlsiConnector(e?.message);
            }
        }

        try {
            const ins = await this._getInsFromIdentityInformation(identityInfos);
            if (!ins) {
                return undefined;
            }
            if (ins.i_insIdentityResult === 1) {
                throw new NoIdentityFound();
            } else if (ins.i_insIdentityResult === 2) {
                return ins;
            } else if (ins.i_insIdentityResult === 3) {
                throw new MultipleIdentitiesFound();
            }
        } catch (e) {
            throw e;
        }
    }

    async getInsFromVitaleCard(view: ViewContainerRef, vitaleIndex: number): Promise<LectureIns> {
        return new Promise<LectureIns>(async (resolve, reject) => {
            /**
             * Le connecteur travaille sur la dernière carte lue :
             * si la carte a changé entre la lecture et la sélection du bénéficiaire,
             * on clôt la session pour forcer une nouvelle authentification (cf. CpxHandleService.getInsFromVitale).
             */
            if (!await this._cpxHandlerSrv.isSameVitaleCard()) {
                try {
                    await this._cpxHandlerSrv.closeSession(await this._getSessionType());
                } catch (e) {
                    console.error(e);
                }
                reject(new NotSameVitaleCard('La carte Vitale a changé depuis la dernière lecture. Veuillez relire la carte Vitale.'));
                return;
            }

            try {
                if (!await this.createTlsiConnector(view)) {
                    resolve(undefined);
                    return;
                }
            } catch (e) {
                console.error(e);
                if (e instanceof DoRetryError) {
                    resolve(await this.getInsFromVitaleCard(view, vitaleIndex));
                    return;
                } else {
                    reject(new CannotCreateTlsiConnector());
                    return;
                }
            }

            try {
                resolve(await this._getInsFromVitaleCard(vitaleIndex));
            } catch (e) {
                if (this._cpxHandlerSrv.isErrorEndSession(e, await this._getSessionType())) {
                    resolve(await this.getInsFromVitaleCard(view, vitaleIndex));
                } else {
                    reject(e);
                }
            }
        });
    }

    public async massCheckInsIdentity(view: ViewContainerRef, param: Array<IdentityToCheck>): Promise<any> {
        const _getSexeIndex = (sexe: string | undefined): 1 | 2 | 3 | undefined => {
          switch (sexe) {
            case 'I':
              return 1;
            case 'H':
              return 2;
            case 'F':
              return 3;
            default:
              return undefined;
          }
        };

        return new Promise<any>(async (resolve, reject) => {
            try {
                if (!await this.createTlsiConnector(view)) {
                    return undefined;
                }
            } catch (e) {
                console.error(e);
                if (e instanceof DoRetryError) {
                    resolve(await this.massCheckInsIdentity(view, param));
                    return;
                } else if (e.message === 'Le cadre d\'exercice n\'est pas renseigné dans les paramètres') {
                    reject(new CadreExerciceIsMissing(e.message));
                } else if (e.i_apiErrorCode === 3
                    && e.i_apiErrorType === 1
                    && e.s_apiErrorContext === 'setConnectionAuthenticationToken') {
                    reject(new CannotCreateTlsiConnector(e?.message));
                } else if (e.message && e.message !==  '') {
                    reject(e);
                } else {
                    reject({ message: 'Une erreur est survenue' });
                }
                return;
            }

            try {
                const identites: Array<MassCheckInsRequestParam> = [];

                for (const i of param) {
                    const sexe = _getSexeIndex(i.sexe);
                    if (sexe && i.ins.length === 13 && i.insKey.length === 2) {
                        identites.push({
                            s_ins: i.ins,
                            s_key: i.insKey,
                            s_oid: i.oid,
                            s_birthName: i.nomDeNaissance.toUpperCase(),
                            s_given: i.prenoms.toUpperCase(),
                            s_birthGiven: i.prenoms.toUpperCase(),
                            i_sex: sexe,
                            s_birthDate: moment(i.dateDeNaissance).format('YYYY-MM-DD'),
                            s_birthPlace: i.cpNaissance || undefined,
                        });
                    }
                }

                this._cpxInstance.hl_submitInsiBatchValidationSet(
                    this.LpsInfos,
                    identites,
                    (response) => {
                        resolve(undefined);
                        // if (response.s_status === 'OK') {
                        //     resolve(true);
                        //     this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Check INS identité' });
                        // } else if (response.error && response.error.i_apiErrorCode === 3
                        //     && response.error.i_apiErrorType === 1 && response.error.s_status === 'ERROR') {
                        //     resolve(false);
                        //     this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Check INS identité' });
                        // } else if (response.error && response.error.i_apiErrorCode === 16384
                        //     && response.error.i_apiErrorType === 6 && response.error.s_status === 'ERROR') {
                        //     resolve(false);
                        //     this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Check INS identité' });
                        // } else {
                        //     reject(response.error);
                        //     this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Check INS identité' });
                        // }
                    }
                );
            } catch (e) {
                reject(e);
                this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Check INS identité' });
            }
        });
    }

    /**
     * Requete de verification de l'identite
     * @param view Container pour l'affichage de popup
     * @param param Parametres du check Insi
     * @returns
     */
    public async checkInsIdentity(view: ViewContainerRef, param: CheckInsRequestParam): Promise<boolean> {
        param.s_birthDate = moment(param.s_birthDate).format('YYYY-MM-DD');
        return new Promise<boolean>(async (resolve, reject) => {
            const objetLog: LogObject = {
              action: ActionEnum.Read,
              result: ResultEnum.Unknown, // temporaire
              typeTarget: TypeTargetEnum.NONE,
              subDomain: CHECK_INS_LOG_SUBDOMAIN
            }

            // Peupleument avec la requete
            objetLog.details = buildCheckInsRequestLogDetails(param, (str) => this._strUtilsSrv.uppercaseWithoutAccents(str));

            try {
                if (!await this.createTlsiConnector(view)) {
                    resolve(undefined);
                    return;
                }
            } catch (e) {
                objetLog.details += "Erreur lors de la création du connecteur Tlsi"
                objetLog.details += '\n' + e.message;
                objetLog.result = ResultEnum.Error;
                this._addLog(objetLog);
                console.error(e);
                if (e instanceof DoRetryError) {
                    resolve(await this.checkInsIdentity(view, param));
                    return;
                } else if (e.message === 'Le cadre d\'exercice n\'est pas renseigné dans les paramètres') {
                    reject(new CadreExerciceIsMissing(e.message));
                } else if (e.i_apiErrorCode === 3
                    && e.i_apiErrorType === 1
                    && e.s_apiErrorContext === 'setConnectionAuthenticationToken') {
                    reject(new CannotCreateTlsiConnector(e?.message));
                } else if (e.message && e.message !==  '') {
                    reject(e);
                } else {
                    reject({ message: 'Une erreur est survenue' });
                }
                return;
                // if (e.message && e.message !== '') {
                //     reject(e);
                // } else if (e.i_apiErrorCode === 3
                //     && e.i_apiErrorType === 1
                //     && e.s_apiErrorContext === 'setConnectionAuthenticationToken') {
                //     reject(new CannotCreateTlsiConnector('Le cadre d\'exercice n\'est pas renseigné dans les paramètres'));
                // } else {
                //     reject({ message: 'Une erreur est survenue' });
                // }
            }



            try {
              this._cpxInstance.hl_checkInsIdentity(
                  this.LpsInfos,
                  param.s_ins,
                  param.s_key,
                  param.s_oid,
                  this._strUtilsSrv.uppercaseWithoutAccents(param.s_birthName),
                  this._strUtilsSrv.uppercaseWithoutAccents(param.s_given),
                  param.i_sex,
                  param.s_birthDate,
                  param.s_birthPlace,
                  async (response) => {
                      // Gestion logs
                      if (response.s_status === 'OK') {
                        objetLog.result = ResultEnum.Success;
                      } else {
                          objetLog.result = ResultEnum.Error;
                          objetLog.details += buildInsiErrorLogDetails(response?.error, true);
                      }



                      if (response.s_status === 'OK') {
                          resolve(true);
                      } else if (this._cpxHandlerSrv.isErrorEndSession(response, await this._getSessionType())) {
                          resolve(await this.checkInsIdentity(view, param));
                      } else if (response.error && response.error.i_apiErrorCode === 3
                          && response.error.i_apiErrorType === 1 && response.error.s_status === 'ERROR') {
                          resolve(false);
                      } else if (response.error && response.error.i_apiErrorCode === 16384
                          && response.error.i_apiErrorType === 6 && response.error.s_status === 'ERROR') {
                          resolve(false);
                      } else {
                          reject(response.error);
                      }
                      this._addLog(objetLog);
                    }
              );
            } catch (e) {
              objetLog.result = ResultEnum.Error;
              this._addLog(objetLog);
              reject(e);
            }
        });
    }

    private async _getInsFromVitaleCard(vitaleIndex: number): Promise<LectureIns>{
        const requeteInfo = buildInsiRequestLogDetails('hl_getInsFromVitaleCard', this.LpsInfos, [`VitaleIndex : ${vitaleIndex}`]);
        return new Promise<LectureIns>((resolve, reject) => {
            this._cpxInstance.hl_getInsFromVitaleCard(
                this.LpsInfos,
                vitaleIndex,
                (response) => {
                    if (response.i_insIdentityResult === 1) {
                        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_VITALE_LOG_SUBDOMAIN, details: INS_NO_IDENTITY_LOG_DETAILS + requeteInfo });
                        reject(new NoIdentityFound());
                    } else if (response.i_insIdentityResult === 3) {
                        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_VITALE_LOG_SUBDOMAIN, details: INS_MULTIPLE_IDENTITIES_LOG_DETAILS + requeteInfo });
                        reject(new MultipleIdentitiesFound());
                    } else if (response.s_status === 'OK') {
                        resolve(response);
                        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_VITALE_LOG_SUBDOMAIN, details: buildIdentityLogDetails(response, INS_UNIQUE_IDENTITY_LOG_HEADER) + requeteInfo });
                    } else {
                        reject(response.error);
                        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_VITALE_LOG_SUBDOMAIN, details: INS_RETOUR_LOG_HEADER + buildInsiErrorLogDetails(response.error) + requeteInfo });
                    }
                }
            );
        });
    }

    private async _getSessionType(): Promise<SessionTypeEnum> {
        const isEs = await this._cpxHandlerSrv.isESConnexionIndirect(SessionTypeEnum.CERT_INSI);
        return isEs ? SessionTypeEnum.CERT_INSI : SessionTypeEnum.CARTE;
    }

    private async createTlsiConnector(view: ViewContainerRef): Promise<boolean> {
        const isEs = await this._cpxHandlerSrv.isESConnexionIndirect(SessionTypeEnum.CERT_INSI);
        const type = isEs ? SessionTypeEnum.CERT_INSI : SessionTypeEnum.CARTE;
        const cpxSession = await this._cpxHandlerSrv.getCpxSession(view, type, TypeConnecteurEnum.DMPConnect);
        if (cpxSession && cpxSession.isSessionOpen && (cpxSession as CpxSessionInterface).isInsiConnectorCreated) {
          return true;
        }

        if (cpxSession) {
            // On récupère et on vérifie la validité des paramètres
            let practiceSettingValue: string;
            const params = await this._resourceSrv.getResourceSettings(this.resourceId, 'profil');
            if (params) {
              for (const param of params) {
                if (param.settingKey === practiceSettingKey) {
                  practiceSettingValue = param.settingValue;
                }
              }
            } else {
              throw {
                message: 'Les paramètres de DMP sont manquants'
              };
            }

            if (!practiceSettingValue || practiceSettingValue === '') {
              throw {
                message: 'Le cadre d\'exercice n\'est pas renseigné dans les paramètres'
              };
            }

            if (!isEs && (cpxSession.practiceSettingIndice === undefined || cpxSession.practiceSettingIndice === null)) {
              throw {
                message: 'Le lieux d\'exercice n\'a pas été renseigné'
              };
            }

            let hpParam : {}
            let practiceLocation: {} = {};

            if (isEs) {
              let professionSettingParam: string;
              let specialiteSettingParam: string;
              let nomSettingParam: string;
              let prenomSettingParam: string;
              let nomStructureSettingParam: string;
              let typeStructureSettingParam: string;
              let contentTypeCodeSettingParam: string;
              let professionSettingOid: string;

              let professionsRef=[];
              const professionSettingsArrTmp = await this._refSrv.findRefListTableHypermed("RefSubjectRoleDMP");
              for (const ps of professionSettingsArrTmp) {
                professionsRef.push({
                  id: ps.id.split('++')[0],
                  oid : ps.sublibelle,
                  libelle: ps.libelle
                });
              }

              const professionSetting = params.find(p => p.settingKey === professionSettingKey);
              if (professionSetting && professionSetting.settingValue !== '') {
                professionSettingParam = professionSetting.settingValue;
                professionSettingOid = professionsRef.find(p => p.id === professionSettingParam).oid;
              } else {
                throw {
                  message: 'La profession n\'est pas renseignée dans les paramètres'
                };
              }

              const specialiteSetting = params.find(p => p.settingKey === specialiteSettingKey);
              if (specialiteSetting && specialiteSetting.settingValue !== '') {
                specialiteSettingParam = specialiteSetting.settingValue;
              } else {
                throw {
                  message: 'La spécialité n\'est pas renseignée dans les paramètres'
                };
              }
              const nomSetting = params.find(p => p.settingKey === nomSettingKey);
              if (nomSetting && nomSetting.settingValue !== '') {
                nomSettingParam = nomSetting.settingValue;
              } else {
                throw {
                  message: 'Le nom n\'est pas renseignée dans les paramètres'
                };
              }
              const prenomSetting = params.find(p => p.settingKey === prenomSettingKey);
              if (prenomSetting && prenomSetting.settingValue !== '') {
                prenomSettingParam = this._strUtilsSrv.uppercaseFirstCharString(prenomSetting.settingValue);
              } else {
                throw {
                  message: 'Le prénom n\'est pas renseignée dans les paramètres'
                };
              }

              const nomStructureSetting = params.find(p => p.settingKey === nomStructureSettingKey);
              if (nomStructureSetting && nomStructureSetting.settingValue !== '') {
                nomStructureSettingParam = nomStructureSetting.settingValue;
              } else {
                throw {
                  message: 'Le nom de structure n\'est pas renseignée dans les paramètres'
                };
              }
              const typeStructureSetting = params.find(p => p.settingKey === typeStructureSettingKey);
              if (typeStructureSetting && typeStructureSetting.settingValue !== '') {
                typeStructureSettingParam = typeStructureSetting.settingValue;
              } else {
                throw {
                  message: 'Le type de structure n\'est pas renseignée dans les paramètres'
                };
              }
              const contentTypeCodeSetting = params.find(p => p.settingKey === contentTypeCodeSettingKey);
              if (contentTypeCodeSetting && contentTypeCodeSetting.settingValue !== '') {
                contentTypeCodeSettingParam = contentTypeCodeSetting.settingValue;
              } else {
                throw {
                  message: 'Le type d\'activité structure n\'est pas renseignée dans les paramètres'
                };
              }

              hpParam = {
                s_hpName: nomSettingParam,
                s_hpGiven: prenomSettingParam,
                s_hpProfession: professionSettingParam,
                s_hpProfessionOid: professionSettingOid,
                s_hpSpeciality: specialiteSettingParam,
                s_hpInternalId: this.resourceId,
                i_hpAuthenticationMode: 9
              }

              practiceLocation = {
                s_practiceLocationName: nomStructureSettingParam,
                s_practiceLocationActivitySector: typeStructureSettingParam,
                s_practiceLocationPracticeSettings: practiceSettingValue,
              }
            }

            const cpxInstance = await this._getCpxInstance(view);
            if (!cpxInstance) {
                return;
            }

            return await new Promise<boolean>(async (resolve, reject) => {
                try {
                  if(isEs) {
                    this.hl_createEsTlsiConnector(
                      cpxInstance,
                      environment.tlsiServerEsUrl,
                      60,
                      hpParam,
                      practiceLocation,
                      {},                // Paramètres optionnels
                      (response) => {
                          if (response.s_status === 'OK') {
                              cpxSession.isSessionOpen = true;
                              this._cpxHandlerSrv.setInsiConnectorIsCreated(type, TypeConnecteurEnum.DMPConnect, true);
                              resolve(true);
                          } else {
                              if (response.error.i_apiErrorCode === 3 && response.error.i_apiErrorType === 6000) {
                                  reject(new DoRetryError());
                              } else {
                                  reject(response.error);
                              }
                          }
                      });

                  } else {
                    if (await this._cpxHandlerSrv.isWirelessMode(SessionTypeEnum.CARTE) && !this._stellairSrv.isStellairAvailable.value) {
                        const token = await this._stellairSrv.getStellairAccessToken();
                        await this._cpxHandlerSrv.updateCpxAuthenticationToken(WirelessModeCardType.CPX, token);
                    }

                    cpxInstance.hl_createTlsiConnector(
                        cpxSession.pinCode,
                        environment.tlsiServerUrl,
                        60,
                        practiceSettingValue,
                        cpxSession.practiceSettingIndice,
                        {
                            PracticeLocation: cpxSession?.cpxType === 'CPF' ? cpxSession.dmpConnectSessionInfos.PracticeLocations[0] : undefined
                        },                // Paramètres optionnels
                        (response) => {
                            if (response.s_status === 'OK') {
                                cpxSession.isSessionOpen = true;
                                resolve(true);
                            } else {
                                if (response.error.i_apiErrorCode === 3 && response.error.i_apiErrorType === 6000) {
                                    reject(new DoRetryError());
                                } else {
                                    reject(response.error);
                                }
                            }
                        }
                    );
                  }

                } catch (e) {
                    throw new Error(e.s_apiErrorDescription);
                }
            });

        } else {
            return false;
        }
    }
  async hl_createEsTlsiConnector(cpxInstance:any,
    tlsiServerUrl: string,
    transactionsTimeoutInSeconds: number,
    hpParam: any,
    practiceLocation: any,
    optionalParameters : {}, resultCallback : (response: any) => void) {


    // Récupération des infos utilisateurs

    // Récupération des infos PracticeLocation


    var serverUrl = "";
    // Empty string => qualification server.
    if( tlsiServerUrl === "" )
        serverUrl = "https://qualiflps-services-ps-tlsm.ameli.fr";
    else
        serverUrl = tlsiServerUrl;

    var command = {
        "s_commandName"               : "hl_createEsTlsiConnector",
        "s_tlsiUrl"                   : serverUrl,
        "i_transactionsTimeout"       : transactionsTimeoutInSeconds,
        "i_getConnectionHandle"       : 1,
        "PracticeLocation"            : practiceLocation,
        "Hp"                          : hpParam,
        "s_sessionId"                 : cpxInstance.getSessionId()
    };

    // Merge the HTTP proxy parameters and/or the NTP proxy parameters set in optionalParameters.
    if( optionalParameters )
    {
        for( var p in optionalParameters )
        {
            command[ p ] = optionalParameters[ p ];
        }
    }

    // Get connection handle
    return cpxInstance.sendCommand( command, 20, resultCallback );
  }

    private async getIns(identityInfos: InsIdentityInfos): Promise<LectureIns> {
        const _findPatients = async (param: InsIdentityInfos): Promise<LectureIns> => {
            const searchedIdentity: InsIdentityInfos = {
                ...param,
                s_birthName: this._strUtilsSrv.uppercaseWithoutAccents(param.s_birthName),
                s_given: this._strUtilsSrv.uppercaseWithoutAccents(param.s_given)
            };
            const searchInfo = buildIdentitySearchLogDetails(searchedIdentity);
            const requeteInfo = buildInsiRequestLogDetails('hl_getInsFromIdentityInformation', this.LpsInfos, buildIdentityRequestLogParams(searchedIdentity));
            return await new Promise<LectureIns>(async (resolve, _) => {
                this._cpxInstance.hl_getInsFromIdentityInformation(
                    this.LpsInfos,
                    this._strUtilsSrv.uppercaseWithoutAccents(param.s_birthName),
                    this._strUtilsSrv.uppercaseWithoutAccents(param.s_given),
                    param.i_sex,
                    param.s_birthDate,
                    param.s_birthPlace,
                    async (response, err) => {
                        if (this._cpxHandlerSrv.isErrorEndSession(response, await this._getSessionType())) {
                            resolve(await this.getIns(identityInfos));
                        } else {
                            resolve(response);
                            if (response.i_insIdentityResult === 1) {
                                this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_IDENTITY_LOG_SUBDOMAIN, details: searchInfo + INS_NO_IDENTITY_LOG_DETAILS + requeteInfo });
                            } else if (response.i_insIdentityResult === 2) {
                                this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_IDENTITY_LOG_SUBDOMAIN, details: searchInfo + buildIdentityLogDetails(response, INS_UNIQUE_IDENTITY_LOG_HEADER) + requeteInfo });
                            } else if (response.i_insIdentityResult === 3) {
                                this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_IDENTITY_LOG_SUBDOMAIN, details: searchInfo + INS_MULTIPLE_IDENTITIES_LOG_DETAILS + requeteInfo });
                            } else if (response.error) {
                                this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: INS_FROM_IDENTITY_LOG_SUBDOMAIN, details: searchInfo + INS_RETOUR_LOG_HEADER + buildInsiErrorLogDetails(response.error) + requeteInfo });
                            }
                        }
                    }
                );
            });
        };

        // const givenSplit = identityInfos.s_given.split(' ');
        // if (givenSplit.length === 1) {
        //     /**
        //      * Si une seul prénom est fourni, on lance la recherche tel quel.
        //      */
        //     return await _findPatients(identityInfos);
        // } else {
        //     /**
        //      * Si plusieurs prénom sont fourni
        //      * on itère que chacun des prénom
        //      * et on renvoi le premier prénom renvoyant un résultat.
        //      */
        //     const searchParam = {...identityInfos};
        //     for (const g of givenSplit) {
        //         if (g !== '') {
        //             searchParam.s_given = g;
        //             const res = await _findPatients(searchParam);
        //             console.log(searchParam.s_given);
        //             console.log(res);
        //             if (!res.error && res.i_insIdentityResult !== 1) {
        //                 return res;
        //             }
        //         }
        //     }

        //     /**
        //      * Si on arrive ici c'est que la recherche de chaque prénom indivuel n'a rien renvoyé.
        //      * On termine donc par une recherche sur l'ensemble des prénom
        //      */
        //     console.log(identityInfos.s_given);
        //     return await _findPatients(identityInfos);
        // }
        return await _findPatients(identityInfos);
    }

    private async _getInsFromIdentityInformation(identityInfos: InsIdentityInfos): Promise<LectureIns | undefined> {
        if (!identityInfos) {
            throw new NoIdentityInfos();
        } else {
            const result = await this.getIns(identityInfos);

            if (result.s_status !== "OK") {
                throw new CannotRetreiveIns(result.error.s_apiErrorExtendedInformations);
            } else if (result.i_insIdentityResult !== 2 || this.isLectureInsOk(result)) {
                // Les résultats 1 (aucune identité) et 3 (plusieurs identités) sont traités par l'appelant
                return result;
            } else {
                return undefined;
            }
        }
    }

    private async _getCpxInstance(view: ViewContainerRef): Promise<any> {
      return new Promise<any>(async (resolve, reject) => {
        try {
            const typeSession = await this._getSessionType();
            if (this.typeSession !== typeSession) {
                this._cpxInstance = undefined;
                this.typeSession = typeSession;
            }

            this._cpxInstance = await this._cpxHandlerSrv.getInstance(view, this.typeSession);

            resolve(this._cpxInstance);
        } catch (e) {
          reject(e);
        }
      });
    }

    private async _addLog(obj: LogObject): Promise<void> {
        if (!this._logCallSrv) {
            this._logCallSrv = this._logSrv.getConfiguration(DomainEnum.Insi);
        }

        this._logCallSrv.addLog(obj);
    }

    // -----
    // Recherche du profil courant
    // -----

    get resourceId(): string {
      if (!this._resourceId) this.getProfilId();

      return this._resourceId;
    }

    set resourceId(value: string) {
      this._resourceId = value;
    }

    private getProfilId() {
      const currentProfilStr = localStorage.getItem('currentProfil');
      if (currentProfilStr && currentProfilStr !== '') {
        const currentProfil = JSON.parse(currentProfilStr);
        this._resourceId = currentProfil.id;
      } else {
        throw Error("Profil introuvable")
      }
    }
}
